import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import BulkShareModal from "@components/show_page/bulk_actions/BulkShareModal";
import BulkDeleteModal from "@components/show_page/bulk_actions/BulkDeleteModal";
import { Brandfolder, Collection, bulkRefreshSections } from '@helpers/show_page_helpers';
import oldType from '@helpers/oldType';
import { removeFromCollection, removeFromLabel } from '@api/v4/bulk_actions/assets';
import downloadAsset from '@api/v3/assets/download';

import MenuItem from './actions_menu_item';

import './styles/actions_menu.scss';

const ActionsMenu = ({
  asset,
  section,
  collectionId,
  canEdit,
  canShare,
  canAnalyze,
  canDelete,
  canRemove,
  canDownload,
  canUnlabel,
  label,
  openDirection,
}) => {
  const [shareModalShown, setShowShareModal] = useState(false);
  const [deleteModalShown, setShowDeleteModal] = useState(false);

  // super annoying, but necessary, since the entire
  // asset card is clickable and interferes with us
  const clickHandler = (action) => (
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      action();
    }
  );

  const onEdit = () => {
    const assetType = oldType(asset.attributes.type);
    const urlBaseString = `${Brandfolder.slug}/sections/${section.key}/ui/${assetType}/${asset.id}?edit=true`;
    const urlParamsString = Collection.slug ? `&collection_slug=${Collection.slug}` : "";
    const url = urlBaseString + urlParamsString;

    const leftArrow = document.getElementsByClassName("left")[0];
    const rightArrow = document.getElementsByClassName("right")[0];
    if (window.getComputedStyle(rightArrow).display === "block") {
      rightArrow.style.display = "none";
      leftArrow.style.display = "none";
    }

    BF.fullModal = true;
    BF.dialog.render(
      encodeURIComponent(`${asset.attributes.name}`),
      url,
      BF.handlerGroups.showAsset,
      "edit"
    );
  };

  const onShare = () => {
    setShowShareModal(true);
  };

  const onAnalyze = () => {
    const collectionSlug = Collection.key ? `/collections/${Collection.slug}` : "";
    const url = `${BFG.INSIGHTS_URL}/${Brandfolder.slug}${collectionSlug}/assets/${asset.id} `;
    window.open(url, '_blank');
  };

  const onDelete = () => {
    setShowDeleteModal(true);
  };

  const onRemove = async () => {
    await removeFromCollection({
      assetKeys: [asset.id],
      collectionKeys: [collectionId],
    });
    bulkRefreshSections([asset.id]);
  };

  const onUnlabel = async () => {
    await removeFromLabel([asset.id], label.key);
    bulkRefreshSections([asset.id]);
  };

  const onDownload = () => {
    const $downloadIndicator = $('.download_progress_indicator');
    const collectionKey = (BF.fx.collection() && BF.fx.collection().key) ? BF.fx.collection().key : "";
    $downloadIndicator.css("visibility", "visible").addClass('active');
    const url = `/api/v3/assets/${asset.id}/download`;
    downloadAsset(asset.id, { collectionKey, manifest: BFG.manifestDigest }).then((response) => {
      if (response.data && !!response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
        $downloadIndicator.css("visibility", "hidden");
        Notify.create({ title: `Downloaded ${response.data.attachment_count} ${BF.fx.should_pluralize(response.data.attachment_count, 'file')}`, type: "success" });
        Insight.createEvent('downloaded', asset.id, 'asset');
      } else {
        BF.BulkAction.result_poller(url.replace('download', 'bulk-convert-download-poller'), response.data, 500);
      }
    }).catch(() => {
      Notify.create({ title: "Something went wrong. Try again soon!", type: "error" });
      $downloadIndicator.css("visibility", "hidden");
    });
  };

  return (
    <React.Fragment>
      <ul
        className={classnames(
          'actions-menu',
          { 'actions-menu--left': openDirection === 'left' },
          { 'padding-bottom': !canDownload },
        )}
      >
        <MenuItem
          text={t('Download')}
          isDisplayed={canDownload && asset.attributes.type !== 'ExternalMedium' && asset.attributes.type !== 'Font'}
          onClick={clickHandler(onDownload)}
          name="download"
          isDownload
        />
        <MenuItem
          text={t('Edit')}
          isDisplayed={canEdit}
          onClick={clickHandler(onEdit)}
          name="edit"
        />
        <MenuItem
          text={t('Share')}
          isDisplayed={canShare}
          onClick={clickHandler(onShare)}
          name="share"
        />
        <MenuItem
          text={t('View Analytics')}
          isDisplayed={canAnalyze}
          onClick={clickHandler(onAnalyze)}
          name="analytics"
        />
        <MenuItem
          text={`${t('Remove from')} ${label ? label.name : ''}`}
          isDisplayed={canUnlabel}
          onClick={clickHandler(onUnlabel)}
          name="unlabel"
        />
        <MenuItem
          text={t('Delete')}
          isDisplayed={canDelete}
          onClick={clickHandler(onDelete)}
          name="delete"
        />
        <MenuItem
          text={t('Remove from Collection')}
          isDisplayed={canRemove}
          onClick={clickHandler(onRemove)}
          name="remove"
        />
      </ul>
      {/*
        you might see this wrapper div and think, "wow, this is really gross"
        ........and you would be right. It is.
        This gets around the fact that the entire container this component usually
        renders into is a giant click handler that gobbles up all our events.
        But two can play at that game! We'll keep it from ever getting events it
        doesn't need to know about, and everyone is a lot happier.
      */}
      <div onClick={(e) => e.stopPropagation()}>
        {shareModalShown ? (
          <BulkShareModal
            selectedAssetKeys={new Set([asset.id])}
            closeModal={() => setShowShareModal(false)}
          />
        ) : null}
        {deleteModalShown ? (
          <BulkDeleteModal
            selectedAssetKeys={new Set([asset.id])}
            closeModal={() => setShowDeleteModal(false)}
            addRemoveSelected={() => {}}
          />
        ) : null}
      </div>
    </React.Fragment>
  );
};

ActionsMenu.propTypes = {
  asset: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  section: PropTypes.shape({
    key: PropTypes.string,
  }),
  collectionId: PropTypes.string,
  canEdit: PropTypes.bool,
  canShare: PropTypes.bool,
  canAnalyze: PropTypes.bool,
  canDelete: PropTypes.bool,
  canRemove: PropTypes.bool,
  canDownload: PropTypes.bool,
  canUnlabel: PropTypes.bool,
  label: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
  }),
  openDirection: PropTypes.oneOf(['left', 'right']),
};

ActionsMenu.defaultProps = {
  section: {},
  collectionId: null,
  openDirection: 'right',
  canEdit: false,
  canShare: false,
  canAnalyze: false,
  canDelete: false,
  canRemove: false,
  canDownload: false,
  canUnlabel: false,
  label: null,
};

export default ActionsMenu;
